import { HIGH_PRECISION_SECURITY_TYPES } from "~constants";
import { getCurrencySymbol } from "~utils/portfolio/getCurrencySymbol";

const THOUSANDS = 1000;
const MILLIONS = THOUSANDS * THOUSANDS;
const BILLIONS = THOUSANDS * MILLIONS;
const CSV_DECIMAL_REGEX = /(\d)(?=(\d{3})+\.)/g;
const CSV_NAME_VALUE_REGEX = /(\d)(?=(?:\d{3})+$)/g;

export function formatDecimals(number: string | number, dp = 2) {
  if (typeof number !== "number") {
    return "";
  }

  return number.toLocaleString("en-US", {
    maximumFractionDigits: dp,
    minimumFractionDigits: dp,
  });
}

export function formatMoney(
  number: string | number,
  dp = 2,
  removeTrailingZeros = false
) {
  if (typeof number !== "number") {
    return number;
  }

  const formattedNumber = formatWithCommasAndDecimals(number, dp);

  if (removeTrailingZeros) {
    return trimDecimalZeros(formattedNumber);
  }

  return formattedNumber;
}

export function formatWithLeadingPlusOrMinus(
  number: string | number,
  currency = ""
) {
  const parsedFloat = typeof number === "string" ? parseFloat(number) : number;

  if (isNaN(parsedFloat)) {
    return "";
  }

  const isPositiveNumber = parsedFloat >= 0;

  if (isPositiveNumber) {
    return `+${currency}${number}`;
  }

  return `${currency}${String(number)}`;
}

export function formatWithCommasAndDecimals(number: string | number, dp = 2) {
  if (typeof number !== "number") {
    return "";
  }

  const numberWithDecimals = formatDecimals(number, dp);

  return toCSV(numberWithDecimals);
}

export function formatMoneyByType(
  number: number,
  type: string,
  removeTrailingZeros = false
) {
  const precision = HIGH_PRECISION_SECURITY_TYPES.has(type) ? 4 : 2;

  return formatMoney(number, precision, removeTrailingZeros);
}

export function formatMoneyWithLeadingPlusOrMinus(number: string | number) {
  const parsedFloat = typeof number === "string" ? parseFloat(number) : number;

  if (isNaN(parsedFloat)) {
    return "";
  }

  const isPositiveNumber = parsedFloat >= 0;

  if (isPositiveNumber) {
    return `+${formatWithCommasAndDecimals(number)}`;
  }

  return `${formatWithCommasAndDecimals(number)}`;
}

export function formatWithCurrencyCommasAndDecimals(
  number: string | number,
  currencyCode?: string,
  dp = 2
) {
  if (typeof number !== "number") {
    return "";
  }

  const currencySymbol = currencyCode ? getCurrencySymbol(currencyCode) : "";
  const numberWithDecimals = formatDecimals(number, dp);

  return `${currencySymbol}${toCSV(numberWithDecimals)}`;
}

function trimDecimalZeros(numberAsString: number | string) {
  if (typeof numberAsString !== "string") return numberAsString;

  const numberStringTrimmedZeros = numberAsString.replace(/0+$/, "");

  if (numberStringTrimmedZeros.slice(-1) === ".")
    return numberStringTrimmedZeros.slice(0, -1);

  return numberStringTrimmedZeros;
}

function toCSV(numberString: number | string) {
  if (typeof numberString !== "string") return numberString;

  if (numberString.indexOf(".") > 0)
    return numberString.replace(CSV_DECIMAL_REGEX, "$1,");

  return numberString.replace(CSV_NAME_VALUE_REGEX, "$1,");
}

export const formatPriceLabel = (value: number, precision = 2) => {
  const absValue = Math.abs(value);
  const options = {
    minimumFractionDigits: precision,
    maximumFractionDigits: precision,
  };

  // capture the sign for non-zero values
  let sign = "";

  if (value < 0) {
    sign = "-";
  } else if (value > 0) {
    sign = "+";
  }

  if (absValue >= BILLIONS) {
    return `${sign}${(absValue / BILLIONS).toLocaleString("en-US", options)}B`;
  }

  if (absValue >= MILLIONS) {
    return `${sign}${(absValue / MILLIONS).toLocaleString("en-US", options)}M`;
  }

  if (absValue >= THOUSANDS) {
    return `${sign}${(absValue / THOUSANDS).toLocaleString("en-US", options)}K`;
  }

  return `${sign}${absValue.toLocaleString("en-US", options)}`;
};
